import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlatformAdminAuth } from '../contexts/PlatformAdminAuthContext';
import { getBaseUrl } from '../../../shared/utils/apiConfig';
import { MetricCard } from '../components/MetricCard';
import { Activity, Server, Zap, TrendingUp } from 'lucide-react';

/**
 * Platform Admin Dashboard
 * 
 * Landing page for platform administrators showing:
 * - System health overview
 * - Tenant statistics
 * - ML infrastructure status
 * - Recent platform activity
 * - Quick actions
 */

interface SystemHealth {
  status: 'healthy' | 'degraded' | 'down';
  services: {
    api: string;
    database: string;
    ml_inference: string;
    redis: string;
  };
}

interface TenantStats {
  total: number;
  active: number;
  inactive: number;
  trial: number;
}

interface ActivityItem {
  id: string;
  type: 'tenant' | 'model' | 'system';
  message: string;
  timestamp: string;
  color: string;
}

interface MLStats {
  models_deployed: number;
  inference_requests_today: number;
  training_jobs_active: number;
  avg_response_time_ms: number;
}

// Helper function to calculate time ago
const getTimeAgo = (timestamp: string): string => {
  const now = new Date().getTime();
  const then = new Date(timestamp).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
};

export const PlatformAdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { platformAdmin } = usePlatformAdminAuth();
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [tenantStats, setTenantStats] = useState<TenantStats | null>(null);
  const [mlStats, setMLStats] = useState<MLStats | null>(null);
  const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem('astrapeak_platform_admin_token');
        if (!token) {
          setLoading(false);
          return;
        }

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        // Fetch real data from backend APIs (platform admin compatible endpoints)
        const [healthRes, mlMetricsRes, trainingJobsRes] = await Promise.all([
          fetch(`${getBaseUrl()}/api/ml/health`, { headers }),
          fetch(`${getBaseUrl()}/api/ml/inference/metrics`, { headers }),
          fetch(`${getBaseUrl()}/api/ml/training/jobs/active`, { headers })
        ]);

        let healthData: any = null;
        let mlData: any = null;
        let trainingJobsData: any = null;

        if (healthRes.ok) {
          healthData = await healthRes.json();
          setSystemHealth({
            status: healthData.status === 'healthy' ? 'healthy' : 'degraded',
            services: {
              api: healthData.components?.api || 'healthy',
              database: healthData.components?.database || 'healthy',
              ml_inference: healthData.components?.ml_inference || 'healthy',
              redis: healthData.components?.redis || 'healthy',
            },
          });
        }

        if (mlMetricsRes.ok) {
          mlData = await mlMetricsRes.json();
        }

        if (trainingJobsRes.ok) {
          trainingJobsData = await trainingJobsRes.json();
        }

        setMLStats({
          models_deployed: mlData?.active_models || 0,
          inference_requests_today: mlData?.total_predictions || 0,
          training_jobs_active: trainingJobsData?.active_count || 0,
          avg_response_time_ms: Math.round(mlData?.average_latency_ms || 0),
        });

        // Fetch tenant stats from platform-admin endpoint
        const tenantsRes = await fetch(`${getBaseUrl()}/api/platform-admin/tenants?limit=1000`, { headers });
        if (tenantsRes.ok) {
          const tenantsData = await tenantsRes.json();
          const tenantsList = tenantsData.tenants || [];
          const total = tenantsList.length;
          // Count active tenants (those with recent activity or not explicitly inactive)
          const active = tenantsList.filter((t: any) => 
            !t.config?.inactive && !t.config?.suspended
          ).length;
          const inactive = total - active;
          const trial = tenantsList.filter((t: any) => 
            t.config?.subscription_type === 'trial' || t.features?.trial_mode
          ).length;
          
          setTenantStats({
            total,
            active,
            inactive,
            trial,
          });

          // Generate activity log from recent tenant data
          const activities: ActivityItem[] = [];
          
          // Add recent tenant activities (last 3 tenants)
          const recentTenants = tenantsList
            .sort((a: any, b: any) => 
              new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
            )
            .slice(0, 3);
          
          recentTenants.forEach((tenant: any, idx: number) => {
            if (tenant.created_at) {
              activities.push({
                id: `tenant-${tenant.id}`,
                type: 'tenant',
                message: `New tenant onboarded: ${tenant.name}`,
                timestamp: tenant.created_at,
                color: 'green'
              });
            }
          });

          // Add ML model activity if available
          const activeModels = mlData?.metrics?.active_models;
          if (activeModels && activeModels > 0) {
            activities.push({
              id: 'ml-models',
              type: 'model',
              message: `${activeModels} ML models deployed and operational`,
              timestamp: new Date().toISOString(),
              color: 'blue'
            });
          }

          // Add system health activity
          if (healthData.status === 'healthy') {
            activities.push({
              id: 'system-health',
              type: 'system',
              message: 'All platform services operational',
              timestamp: new Date().toISOString(),
              color: 'purple'
            });
          }

          // Sort by timestamp descending and take top 5
          const sortedActivities = activities
            .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
            .slice(0, 5);
          
          setRecentActivity(sortedActivities);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-white text-xl">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 border border-purple-700/50 rounded-lg p-6">
        <h1 className="text-3xl font-semibold text-white mb-2">
          Welcome back, {platformAdmin?.username}!
        </h1>
        <p className="text-gray-300">
          {platformAdmin?.is_super_admin ? 'Super Administrator' : 'Platform Administrator'} • 
          System-wide access to all tenants and infrastructure
        </p>
      </div>

      {/* System Health Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-400">System Status</h3>
            <div className={`w-3 h-3 rounded-full ${
              systemHealth?.status === 'healthy' ? 'bg-green-400' : 'bg-red-400'
            } animate-pulse`}></div>
          </div>
          <p className="text-2xl font-semibold text-white capitalize">{systemHealth?.status}</p>
          <p className="text-sm text-gray-400 mt-1">All services operational</p>
        </div>

        {/* ✅ Clickable - navigates to /platform-admin/tenants/list */}
        <button
          onClick={() => navigate('/platform-admin/tenants/list')}
          className="bg-gray-800 border border-gray-700 rounded-lg p-6 hover:bg-gray-750 transition-colors text-left w-full"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-400">Total Tenants</h3>
            <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <p className="text-2xl font-semibold text-white">{tenantStats?.total}</p>
          <p className="text-sm text-green-400 mt-1">{tenantStats?.active} active</p>
        </button>

        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-400">ML Models</h3>
            <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <p className="text-2xl font-semibold text-white">{mlStats?.models_deployed}</p>
          <p className="text-sm text-gray-400 mt-1">Deployed across tenants</p>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-400">Inference Requests</h3>
            <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
          <p className="text-2xl font-semibold text-white">{mlStats?.inference_requests_today.toLocaleString()}</p>
          <p className="text-sm text-gray-400 mt-1">Today • {mlStats?.avg_response_time_ms}ms avg</p>
        </div>
      </div>

      {/* Service Health Details */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-white mb-4">Service Health</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(systemHealth?.services || {}).map(([service, status]) => (
            <div key={service} className="flex items-center justify-between p-4 bg-gray-900 rounded-md">
              <div>
                <p className="text-sm font-medium text-white capitalize">
                  {service.replace('_', ' ')}
                </p>
                <p className="text-xs text-gray-400 mt-1 capitalize">{status}</p>
              </div>
              <div className={`w-2 h-2 rounded-full ${
                status === 'healthy' ? 'bg-green-400' : 'bg-red-400'
              }`}></div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* ✅ Connected to /platform-admin/tenants/create */}
          <button 
            onClick={() => navigate('/platform-admin/tenants/create')}
            className="flex items-center space-x-3 p-4 bg-gray-900 hover:bg-gray-700 rounded-md transition-colors text-left"
          >
            <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <div>
              <p className="text-sm font-medium text-white">Add New Tenant</p>
              <p className="text-xs text-gray-400">Provision new organization</p>
            </div>
          </button>

          {/* ✅ Connected to /platform-admin/tenants/usage */}
          <button 
            onClick={() => navigate('/platform-admin/tenants/usage')}
            className="flex items-center space-x-3 p-4 bg-gray-900 hover:bg-gray-700 rounded-md transition-colors text-left"
          >
            <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-white">View Analytics</p>
              <p className="text-xs text-gray-400">Platform-wide metrics</p>
            </div>
          </button>

          {/* ✅ Connected to /platform-admin/system/platform-settings */}
          <button 
            onClick={() => navigate('/platform-admin/system/platform-settings')}
            className="flex items-center space-x-3 p-4 bg-gray-900 hover:bg-gray-700 rounded-md transition-colors text-left"
          >
            <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-white">Platform Settings</p>
              <p className="text-xs text-gray-400">Configure system-wide settings</p>
            </div>
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-white mb-4">Recent Platform Activity</h2>
        {recentActivity.length > 0 ? (
          <div className="space-y-3">
            {recentActivity.map((activity) => {
              const timeAgo = getTimeAgo(activity.timestamp);
              const colorClass = `bg-${activity.color}-400`;
              
              return (
                <div key={activity.id} className="flex items-center justify-between p-3 bg-gray-900 rounded-md">
                  <div className="flex items-center space-x-3">
                    <div className={`w-2 h-2 ${colorClass} rounded-full`}></div>
                    <div>
                      <p className="text-sm text-white">{activity.message}</p>
                      <p className="text-xs text-gray-400">{timeAgo}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-400 text-sm">No recent activity</p>
        )}
      </div>
    </div>
  );
};

export default PlatformAdminDashboard;
